/**
 * Onboarding modal for first-time users to select a persona
 */

import { useState } from "react";

import { useUserPreferences } from "../../hooks/useUserPreferences";
import type { PersonaId } from "../../types/persona";
import { PersonaAvatar } from "../persona/PersonaAvatar";
import { PersonaSelector } from "../persona/PersonaSelector";

interface PersonaOnboardingProps {
  onComplete: () => void;
}

export function PersonaOnboarding({ onComplete }: PersonaOnboardingProps) {
  const { personas, isLoadingPersonas, completeOnboarding, isCompletingOnboarding } =
    useUserPreferences();

  const [selectedPersonaId, setSelectedPersonaId] = useState<PersonaId>("akela");

  const handleSelect = (personaId: PersonaId) => {
    setSelectedPersonaId(personaId);
  };

  const handleConfirm = () => {
    completeOnboarding(selectedPersonaId, {
      onSuccess: () => {
        onComplete();
      },
    });
  };

  // Get selected persona details for preview
  const selectedPersonaDetails = personas.find((p) => p.personaId === selectedPersonaId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-scrim">
      <div className="bg-surface-container-lowest rounded-lg shadow-5 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-primary text-on-primary p-6 rounded-t-lg">
          <h1 className="text-2xl md:text-3xl font-bold text-center">🐺 Welkom bij Akela!</h1>
          <p className="text-center mt-2 text-on-primary/80 text-lg">Wie wil je spreken?</p>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-on-surface-variant text-center mb-6">
            Kies een personage dat bij jou past. Elk personage heeft een eigen communicatiestijl,
            van formeel tot speels.
          </p>

          {/* Persona grid */}
          <PersonaSelector
            personas={personas}
            selectedId={selectedPersonaId}
            onSelect={handleSelect}
            isLoading={isLoadingPersonas}
            showDescriptions={false}
          />

          {/* Selected persona description */}
          {selectedPersonaDetails && (
            <div className="mt-6 p-4 bg-primary-container/40 rounded-md border border-primary/20">
              <div className="flex items-center gap-3 mb-2">
                <PersonaAvatar persona={selectedPersonaDetails} size="md" />
                <div>
                  <h3 className="font-semibold text-on-primary-container">
                    {selectedPersonaDetails.emoji} {selectedPersonaDetails.name}
                  </h3>
                  <p className="text-sm text-on-primary-container/80">
                    {selectedPersonaDetails.description}
                  </p>
                </div>
              </div>
              <p className="text-sm text-on-surface-variant">
                {selectedPersonaDetails.toneDescription}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-surface-container-low p-6 rounded-b-lg border-t border-outline-variant">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-on-surface-variant text-center sm:text-left">
              💡 Je kunt dit later altijd aanpassen in je profiel
            </p>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isCompletingOnboarding}
              className="
                px-8 py-3 bg-primary text-on-primary font-semibold rounded-md
                hover:brightness-110 transition-colors shadow-2
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center gap-2
              "
            >
              {isCompletingOnboarding ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Bezig...
                </>
              ) : (
                <>
                  Start met {selectedPersonaDetails?.name ?? "Akela"}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
